import { createSelector } from '@reduxjs/toolkit';
import { hasOrgWideRole, isReadOnlyProfile } from 'config/rolesConfig';

// ── Global drawers ─────────────────────────────────────────
export const selectDrawerStack = (state) => state.App.drawers;
export const selectTopDrawerKey = (state) => {
	const drawers = state.App.drawers;
	return drawers.length ? drawers[drawers.length - 1]._key : null;
};

// Selected dashboard shell ('access', 'lockers', …).
export const selectSelectedDashboard = (state) => state.App.dashboard.selected;

// Active org from auth profile
const selectActiveOrgID = (state) => state.Auth?.selectedProfile?.orgID ?? null;

// True when the user holds an org-wide role: every site, and may act org-wide (no site).
export const selectHasOrgWideRole = (state) => hasOrgWideRole(state.Auth?.selectedProfile?.roles);

// True when the user's roles are all read-only: hide or disable write controls the server would refuse.
export const selectIsReadOnlyProfile = (state) => isReadOnlyProfile(state.Auth?.selectedProfile?.roles);

// Sites for the active org (Sites.data is keyed by orgID → array of sites)
const selectSitesByOrgID = (state) => state.Sites?.data ?? {};

const selectSelectedSiteIDRaw = (state) => state.App.selectedSiteID;
const selectSiteScopeHydrated = (state) => state.App.siteScopeHydrated;

// Public selectors

// Tiny passthrough so callers can `useSelector(selectSelectedSiteID)` instead
// of reaching into state.App.selectedSiteID by hand at every site-scoped call site.
export const selectSelectedSiteID = selectSelectedSiteIDRaw;

export const selectScope = createSelector(
	[selectActiveOrgID, selectSelectedSiteIDRaw, selectSiteScopeHydrated],
	(orgID, selectedSiteID, siteScopeHydrated) => ({ orgID, selectedSiteID, siteScopeHydrated })
);

export const selectIsPortfolioMode = createSelector(
	[selectSelectedSiteIDRaw, selectSiteScopeHydrated],
	(selectedSiteID, hydrated) => hydrated && selectedSiteID === 'all'
);

export { selectSiteScopeHydrated as selectScopeHydrated };

export const selectSelectedSite = createSelector(
	[selectActiveOrgID, selectSelectedSiteIDRaw, selectSitesByOrgID],
	(orgID, selectedSiteID, sitesByOrg) => {
		if (selectedSiteID === 'all' || orgID == null) return null;
		const sites = sitesByOrg[orgID] ?? [];
		return sites.find((s) => s.siteID === selectedSiteID) ?? null;
	}
);

// The picked site's ID, or null under All sites (or a site outside the active org).
export const selectScopedSiteID = (state) => selectSelectedSite(state)?.siteID ?? null;

// Convenience: sites for the active org as a flat array (or empty)
export const selectActiveOrgSites = createSelector([selectActiveOrgID, selectSitesByOrgID], (orgID, sitesByOrg) =>
	orgID == null ? [] : (sitesByOrg[orgID] ?? [])
);

// ── Scoped Dashboard Ready Selectors ────────────────────────────────

const selectSiteID = (state) => state.Auth?.selectedProfile?.siteID ?? 'all';

// A bucket is ready once it has loaded at least once: a refetch must not swap the page for the
// full-screen loader (that remounted every page). A missing bucket answers false.
const bucketReady = (sliceData, orgID, siteID) => {
	const bucket = sliceData?.[orgID]?.[siteID];
	return Boolean(bucket && (bucket.loaded || !bucket.loading));
};

// 🛑 No camera clause: cameras are device rows, so `Devices` already gates them. A module-flag camera
// wait hung the app on org switch (the read was never dispatched). Access dashboard fully loaded:
export const selectAccessDashboardReady = createSelector(
	[
		selectActiveOrgID,
		selectSiteID,
		(state) => state.Sites?.sitesLoadingComplete ?? false,
		(state) => state.Floors?.floorsLoadingComplete ?? false,
		(state) => state.Categories?.categoriesLoadingComplete ?? false,
		(state) => state.People.data,
		(state) => state.Teams.data,
		(state) => state.Devices.data,
		(state) => state.Credentials.data,
		(state) => state.Access.data
	],
	(orgID, siteID, sitesReady, floorsReady, categoriesReady, people, teams, devices, credentials, permissions) => {
		if (!orgID) return false;
		return (
			sitesReady &&
			floorsReady &&
			categoriesReady &&
			bucketReady(people, orgID, siteID) &&
			bucketReady(teams, orgID, siteID) &&
			bucketReady(devices, orgID, siteID) &&
			bucketReady(credentials, orgID, siteID) &&
			bucketReady(permissions, orgID, siteID)
		);
	}
);

// Label for the current bootstrap stage (first resource not ready, in load order) for the loading
// screen; mirrors selectAccessDashboardReady so both settle at the same moment.
export const selectAccessBootstrapStage = createSelector(
	[
		selectActiveOrgID,
		selectSiteID,
		(state) => state.Sites?.sitesLoadingComplete ?? false,
		(state) => state.Floors?.floorsLoadingComplete ?? false,
		(state) => state.Categories?.categoriesLoadingComplete ?? false,
		(state) => state.People.data,
		(state) => state.Teams.data,
		(state) => state.Devices.data,
		(state) => state.Credentials.data,
		(state) => state.Access.data
	],
	(orgID, siteID, sitesReady, floorsReady, categoriesReady, people, teams, devices, credentials, permissions) => {
		if (!orgID) return '';
		if (!sitesReady) return 'Sites';
		if (!floorsReady) return 'Floors';
		if (!categoriesReady) return 'Categories';
		if (!bucketReady(people, orgID, siteID)) return 'People';
		if (!bucketReady(teams, orgID, siteID)) return 'Teams';
		// Cameras are not named separately — they arrive ON this read, so "Devices" already
		// covers them and a second stage would announce a request that does not exist.
		if (!bucketReady(devices, orgID, siteID)) return 'Devices';
		if (!bucketReady(credentials, orgID, siteID)) return 'Credentials';
		if (!bucketReady(permissions, orgID, siteID)) return 'Access';
		return '';
	}
);

// Returns true when at least one scoped entity is still loading
export const selectScopeLoading = createSelector(
	[
		selectActiveOrgID,
		selectSiteID,
		(state) => state.People.data,
		(state) => state.Teams.data,
		(state) => state.Devices.data,
		(state) => state.Credentials.data,
		(state) => state.Access.data
	],
	(orgID, siteID, people, teams, devices, credentials, permissions) => {
		if (!orgID) return true;
		const isLoading = (sliceData) => sliceData[orgID]?.[siteID]?.loading ?? true;
		return (
			isLoading(people) ||
			isLoading(teams) ||
			isLoading(devices) ||
			isLoading(credentials) ||
			isLoading(permissions)
		);
	}
);

// True when the Lockers shell can PAINT: the entities its grid is built from. The 90-day log
// window is deliberately absent — its cards carry their own loading state.
export const selectLockersDashboardReady = createSelector(
	[
		selectActiveOrgID,
		(state) => state.Sites?.sitesLoadingComplete ?? false,
		(state) => state.Floors?.floorsLoadingComplete ?? false,
		(state) => state.Categories?.categoriesLoadingComplete ?? false,
		(state) => state.People.data,
		(state) => state.Devices.data,
		(state) => state.Bookings?.bookingsLoadingComplete ?? false,
		(state) => state.Maintenance?.maintenanceLoadingComplete ?? false
	],
	(orgID, sitesReady, floorsReady, categoriesReady, people, devices, bookingsReady, maintenanceReady) => {
		if (!orgID) return false;
		return (
			sitesReady &&
			floorsReady &&
			categoriesReady &&
			bucketReady(people, orgID, 'all') &&
			bucketReady(devices, orgID, 'all') &&
			bookingsReady &&
			maintenanceReady
		);
	}
);
